import { can, type AuthorizedUser } from '@dma/auth';
import { AuthorizationError, ApplicationError, ValidationError, type Logger } from '@dma/shared';
import { generateRequestId } from '@dma/utils';
import { ScientificEntry } from '../../domain/entities/ScientificEntry.entity';
import { InvalidScientificEntryError } from '../../domain/errors/InvalidScientificEntryError';
import type { ScientificEntryRepository } from '../../domain/repositories/ScientificEntryRepository.interface';
import type { SaveScientificEntryDto } from '../dto/SaveScientificEntryDto';
import type { ScientificEntryDto } from '../dto/ScientificEntryDto';

/**
 * Handles both create (input.id === null) and update (input.id set) — the
 * admin form is identical either way, so one use case avoids duplicating the
 * same validation/save flow for what is, from the domain's point of view,
 * a single "here is the current correct state of this entry" operation.
 */
export class SaveScientificEntryUseCase {
  constructor(
    private readonly repository: ScientificEntryRepository,
    private readonly logger: Logger
  ) {}

  public async execute(actingUser: AuthorizedUser | null, input: SaveScientificEntryDto): Promise<ScientificEntryDto> {
    if (!can(actingUser, 'scientific-entry:manage')) {
      throw new AuthorizationError();
    }

    const now = new Date().toISOString();
    const isUpdate = input.id !== null;
    const existing = isUpdate ? await this.repository.getById(input.id as string) : null;

    if (isUpdate && !existing) {
      throw new ApplicationError('SCIENTIFIC_ENTRY_NOT_FOUND', 'مطلب مورد نظر برای ویرایش یافت نشد.');
    }

    let entry: ScientificEntry;
    try {
      entry = ScientificEntry.create({
        ...input,
        id: existing?.props.id ?? generateRequestId(),
        createdAt: existing?.props.createdAt ?? now,
        updatedAt: now
      });
    } catch (error) {
      if (error instanceof InvalidScientificEntryError) {
        throw new ValidationError(error.message);
      }
      throw error;
    }

    try {
      await this.repository.save(entry);
    } catch (error) {
      this.logger.error('Failed to persist scientific entry', {
        entryId: entry.props.id,
        cause: error instanceof Error ? error.message : String(error)
      });
      throw new ApplicationError('SCIENTIFIC_ENTRY_SAVE_FAILED', 'ذخیره‌ی مطلب با مشکل مواجه شد. لطفاً دوباره تلاش کنید.', {
        cause: error
      });
    }

    return entry.props;
  }
}
